import { useCallback, useEffect, useRef, useState } from "react";
import { cx } from "@its-engineering-and-development/ui-foundation/utils";
import { type DateFormatConfig, TABLE_DATE_FORMAT_CONFIG } from "@/constants/date-formats";
import { autoFormatDateInput, formatIsoDateForInput, parseDisplayDateToIso } from "@/utils/date";

function IconCalendar() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
    );
}

export interface DateInputFilterProps {
    value: string | null | undefined;
    onChange: (isoValue: string | null) => void;
    /** Override the displayed placeholder. Defaults to `dateFormat.displayLabel`. */
    placeholder?: string;
    className?: string;
    error?: string;
    ariaLabel?: string;
    autoFocus?: boolean;
    /**
     * Controls which date format is shown and parsed.
     * Defaults to MM/DD/YYYY (TABLE_DATE_FORMAT_CONFIG).
     * Pass a custom DateFormatConfig to support other formats (e.g. DD/MM/YYYY).
     */
    dateFormat?: DateFormatConfig;
}

export function DateInputFilter({
    value,
    onChange,
    placeholder,
    className,
    error,
    ariaLabel,
    autoFocus = false,
    dateFormat = TABLE_DATE_FORMAT_CONFIG,
}: DateInputFilterProps) {
    const [inputValue, setInputValue] = useState<string>("");
    const hiddenDateRef = useRef<HTMLInputElement>(null);
    // Prevents blur from firing validation when the calendar button is clicked
    const skipNextBlurRef = useRef(false);

    // Sync display value when the external ISO value changes (e.g. saved filter recalled)
    useEffect(() => {
        setInputValue(formatIsoDateForInput(value, dateFormat));
    }, [value, dateFormat]);

    // Auto-format digits as user types — separators are inserted per dateFormat.slotSizes
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.currentTarget.value;
            // When deleting, pass the raw value through without reformatting
            if (raw.length < inputValue.length) {
                setInputValue(raw);
                return;
            }
            setInputValue(autoFormatDateInput(raw, dateFormat));
        },
        [inputValue, dateFormat],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (skipNextBlurRef.current) {
                skipNextBlurRef.current = false;
                return;
            }
            const raw = e.currentTarget.value.trim();
            if (!raw) {
                setInputValue("");
                onChange(null);
                return;
            }
            const isoValue = parseDisplayDateToIso(raw, dateFormat);
            if (isoValue) {
                onChange(isoValue);
                setInputValue(formatIsoDateForInput(isoValue, dateFormat));
            } else {
                // Invalid date — clear the active filter so the parent stays in sync
                onChange(null);
            }
        },
        [onChange, dateFormat],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Escape") {
                setInputValue(formatIsoDateForInput(value, dateFormat));
                return;
            }
            if (e.key === "Enter") {
                e.preventDefault();
                const trimmed = inputValue.trim();
                if (!trimmed) {
                    setInputValue("");
                    onChange(null);
                    e.currentTarget.blur();
                    return;
                }
                const isoValue = parseDisplayDateToIso(trimmed, dateFormat);
                if (isoValue) {
                    onChange(isoValue);
                    setInputValue(formatIsoDateForInput(isoValue, dateFormat));
                    e.currentTarget.blur();
                }
            }
        },
        [value, inputValue, onChange, dateFormat],
    );

    // Open the native browser calendar picker programmatically
    const handleCalendarClick = useCallback(() => {
        const picker = hiddenDateRef.current;
        if (!picker) return;
        try {
            picker.showPicker();
        } catch {
            picker.click();
        }
    }, []);

    // When user selects a date from the calendar, sync to display format
    const handleHiddenDateChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const iso = e.currentTarget.value; // native date input always yields YYYY-MM-DD
            if (iso) {
                setInputValue(formatIsoDateForInput(iso, dateFormat));
                onChange(iso);
            }
        },
        [onChange, dateFormat],
    );

    // Pre-select the currently committed date in the calendar picker
    const currentIso = parseDisplayDateToIso(inputValue, dateFormat) ?? (value || "");

    return (
        <div className={cx("relative", className)}>
            <input
                autoFocus={autoFocus}
                type="text"
                value={inputValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder ?? dateFormat.displayLabel}
                aria-label={ariaLabel ?? "Date filter"}
                className={cx(
                    "w-full rounded-md border py-2 pr-8 pl-3 text-sm ring-0 outline-none placeholder:text-gray-400 focus:border-blue-500",
                    error ? "border-red-300 focus:border-red-500" : "border-blue-500",
                )}
            />
            {/* Calendar icon — sets skipNextBlurRef before blur fires, then opens the picker */}
            <button
                type="button"
                tabIndex={-1}
                onMouseDown={() => {
                    skipNextBlurRef.current = true;
                }}
                onClick={handleCalendarClick}
                className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-gray-400 hover:text-gray-600"
                aria-label="Open calendar"
            >
                <IconCalendar />
            </button>
            {/* Hidden native date input — provides the browser calendar picker UI only */}
            <input
                ref={hiddenDateRef}
                type="date"
                value={currentIso}
                onChange={handleHiddenDateChange}
                className="pointer-events-none absolute right-0 top-0 h-0 w-0 opacity-0"
                tabIndex={-1}
                aria-hidden="true"
            />
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    );
}
